import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import {
  menuItems,
  sidebarGroups,
  sidebarSubGroups,
  type MenuGroup,
  type MenuItem,
  type SubGroup,
} from './menuItems'
import { useAuth } from '@/auth/useAuth'
import { useTenantSettings } from '@/features/company/queries'
import { SMB_ONLY } from '@/config/productScope'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/cn'
import { TenantSwitcher } from './TenantSwitcher'
import { useFeatures } from '@/auth/useFeatures'

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { hasPermission, isPlatformAdmin } = useAuth()
  const { hasFeature, isLoading: featuresLoading } = useFeatures()
  const location = useLocation()
  const tenant = useTenantSettings()
  const logoUrl = tenant.data?.tenant?.logo_url || '/images/logo.png'
  const businessName =
    tenant.data?.tenant?.business_name || tenant.data?.tenant?.name || 'Bokapsys'

  const visible = useMemo(
    () =>
      menuItems.filter((item) => {
        if (SMB_ONLY && item.smbHidden) return false
        if (item.platformOnly && !isPlatformAdmin) return false
        if (item.permission && !hasPermission(item.permission)) return false
        // Feature-flag gate: if the item declares a `feature`, hide
        // it until the tenant's feature map says it's enabled. While
        // the feature map is loading on first render, hide so we
        // don't briefly show + then yank items.
        if (item.feature) {
          if (featuresLoading) return false
          if (!hasFeature(item.feature as Parameters<typeof hasFeature>[0])) return false
        }
        return true
      }),
    [hasPermission, isPlatformAdmin, hasFeature, featuresLoading],
  )

  const standalone = useMemo(() => visible.filter((i) => i.group === 'standalone'), [visible])
  const platform = useMemo(() => visible.filter((i) => i.group === 'platform'), [visible])
  const footer = useMemo(() => visible.filter((i) => i.group === 'footer'), [visible])

  const byGroup = useMemo(() => {
    const map: Record<MenuGroup, MenuItem[]> = {
      standalone: [],
      statements: [],
      accounting: [],
      banking: [],
      invoicing: [],
      payroll: [],
      tax: [],
      account: [],
      platform: [],
      footer: [],
    }
    for (const item of visible) map[item.group].push(item)
    return map
  }, [visible])

  // Accordion state - only one group open at a time, matching the legacy
  // dashboard sidebar. Auto-expand the group that contains the current route
  // so the active link is always visible after navigation.
  // Pick the LONGEST matching item so `/tax/filings/payroll` resolves to the
  // "Payroll Filings" row (subGroup='tax-filings') instead of falling back to
  // `/tax` (no subgroup) and collapsing the sub-accordion on every navigation.
  const activeMatch = useMemo<MenuItem | null>(() => {
    let best: MenuItem | null = null
    for (const item of visible) {
      if (!location.pathname.startsWith(item.path)) continue
      if (!best || item.path.length > best.path.length) best = item
    }
    return best
  }, [visible, location.pathname])

  const activeGroup = useMemo<MenuGroup | null>(() => {
    if (!activeMatch) return null
    if (activeMatch.group === 'standalone' || activeMatch.group === 'platform') return null
    return activeMatch.group
  }, [activeMatch])

  const activeSubGroup = useMemo<SubGroup | null>(() => activeMatch?.subGroup ?? null, [activeMatch])

  const [openGroup, setOpenGroup] = useState<MenuGroup | null>(activeGroup)
  const [openSubGroup, setOpenSubGroup] = useState<SubGroup | null>(activeSubGroup)

  useEffect(() => {
    if (activeGroup) setOpenGroup(activeGroup)
  }, [activeGroup])
  useEffect(() => {
    if (activeSubGroup) setOpenSubGroup(activeSubGroup)
  }, [activeSubGroup])

  const toggleGroup = (key: MenuGroup) => {
    setOpenGroup((prev) => (prev === key ? null : key))
  }
  const toggleSubGroup = (key: SubGroup) => {
    setOpenSubGroup((prev) => (prev === key ? null : key))
  }

  const linkClasses = (isActive: boolean, depth = 0) =>
    cn(
      'relative flex items-center gap-2.5 rounded-md py-2.5 text-sm font-medium transition-colors',
      'hover:bg-sidebar-accent hover:text-foreground',
      depth === 0 && 'px-3',
      depth === 1 && 'pl-9 pr-3',
      depth === 2 && 'pl-14 pr-3',
      // Active: emerald wash + bold weight + a 3px primary bar on the left edge.
      // Mirrors Donezo's "vertical accent" treatment.
      isActive
        ? 'bg-sidebar-accent font-semibold text-primary before:absolute before:left-0 before:top-1/2 before:h-5 before:w-1 before:-translate-y-1/2 before:rounded-pill before:bg-primary'
        : 'text-sidebar-foreground',
    )

  const renderLink = (item: MenuItem, depth: number) => {
    if (item.ported) {
      return (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={onNavigate}
          className={({ isActive }) => linkClasses(isActive, depth)}
        >
          <item.icon className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{item.label}</span>
        </NavLink>
      )
    }
    return (
      <a
        key={item.path}
        href={item.path}
        onClick={onNavigate}
        className={linkClasses(false, depth)}
        title="Legacy page - opens outside the React app"
      >
        <item.icon className="size-4 shrink-0 opacity-70" aria-hidden />
        <span className="truncate opacity-80">{item.label}</span>
      </a>
    )
  }

  return (
    <aside className="flex h-full w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-16 items-center justify-between gap-2 px-3 py-2">
        <NavLink
          to="/company"
          onClick={onNavigate}
          className="flex min-w-0 flex-1 items-center justify-center"
          aria-label={`${businessName} - company settings`}
        >
          {/*
            Uploaded tenant logos are often square/small - scale them up to
            the full available header (~48-52px tall, ~210px wide) so the
            brand is legible. object-contain keeps the aspect ratio; we also
            fall back to the default Bokapsys logo if the signed URL fails
            to load (signed URL expiry, broken S3 object, etc.).
          */}
          <img
            src={logoUrl}
            alt={businessName}
            onError={(e) => {
              const img = e.currentTarget
              if (img.src.indexOf('/images/logo.png') === -1) img.src = '/images/logo.png'
            }}
            className="h-full max-h-12 w-auto max-w-[210px] object-contain"
          />
        </NavLink>
        {/*
          Tenant switcher rides alongside the logo so switching workspace
          is always one click away. Hidden for single-membership users by
          the component itself, so the layout collapses cleanly when it
          renders nothing.
        */}
        <TenantSwitcher />
      </div>

      <ScrollArea className="flex-1">
        <nav className="flex flex-col gap-1 p-3">
          {standalone.map((item) => renderLink(item, 0))}

          {sidebarGroups.map((group) => {
            const items = byGroup[group.key]
            if (items.length === 0) return null
            const expanded = openGroup === group.key
            const GroupIcon = group.icon
            const subGroupsForThis = sidebarSubGroups.filter((sg) => sg.parent === group.key)

            // Build a render-order list that preserves the original menuItems
            // ordering: direct items render in place, and a sub-group header
            // + children render at the position of its first child.
            type Renderable =
              | { kind: 'item'; item: MenuItem }
              | { kind: 'subGroup'; key: SubGroup; items: MenuItem[] }
            const renderables: Renderable[] = []
            const emittedSub = new Set<SubGroup>()
            for (const item of items) {
              if (item.subGroup) {
                if (emittedSub.has(item.subGroup)) continue
                emittedSub.add(item.subGroup)
                const siblings = items.filter((i) => i.subGroup === item.subGroup)
                renderables.push({ kind: 'subGroup', key: item.subGroup, items: siblings })
              } else {
                renderables.push({ kind: 'item', item })
              }
            }

            return (
              <div key={group.key} className="mt-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={expanded}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    'hover:bg-sidebar-accent hover:text-foreground',
                    expanded ? 'text-foreground' : 'text-sidebar-foreground',
                  )}
                >
                  <GroupIcon className="size-4 shrink-0" aria-hidden />
                  <span className="flex-1 truncate text-left">{group.label}</span>
                  <ChevronRight
                    className={cn(
                      'size-4 shrink-0 transition-transform',
                      expanded && 'rotate-90',
                    )}
                    aria-hidden
                  />
                </button>

                {expanded ? (
                  <div className="mt-1 space-y-1">
                    {renderables.map((r) => {
                      if (r.kind === 'item') return renderLink(r.item, 1)
                      const sg = subGroupsForThis.find((s) => s.key === r.key)
                      if (!sg) return null
                      if (sg.permission && !hasPermission(sg.permission)) return null
                      const sgExpanded = openSubGroup === sg.key
                      const SgIcon = sg.icon
                      return (
                        <div key={sg.key}>
                          <button
                            type="button"
                            onClick={() => toggleSubGroup(sg.key)}
                            aria-expanded={sgExpanded}
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md py-2 pl-9 pr-3 text-sm font-medium transition-colors',
                              'hover:bg-sidebar-accent hover:text-foreground',
                              sgExpanded ? 'text-foreground' : 'text-sidebar-foreground',
                            )}
                          >
                            <SgIcon className="size-4 shrink-0" aria-hidden />
                            <span className="flex-1 truncate text-left">{sg.label}</span>
                            <ChevronRight
                              className={cn(
                                'size-4 shrink-0 transition-transform',
                                sgExpanded && 'rotate-90',
                              )}
                              aria-hidden
                            />
                          </button>
                          {sgExpanded ? (
                            <div className="mt-1 space-y-1">
                              {r.items.map((item) => renderLink(item, 2))}
                            </div>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            )
          })}

          {platform.length > 0 ? (
            <div className="mt-3 border-t border-sidebar-border pt-3">
              {platform.map((item) => renderLink(item, 0))}
            </div>
          ) : null}
        </nav>
      </ScrollArea>

      {footer.length > 0 ? (
        <div className="flex shrink-0 flex-col gap-1 border-t border-sidebar-border p-3">
          {footer.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={({ isActive }) => linkClasses(isActive, 0)}
            >
              <item.icon className="size-4 shrink-0" aria-hidden />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </div>
      ) : null}

      <div className="shrink-0 border-t border-sidebar-border px-3 py-2 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <a
            href="https://bokapsys.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground hover:underline"
          >
            Privacy Policy
          </a>
          <span aria-hidden>·</span>
          <a
            href="https://bokapsys.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground hover:underline"
          >
            Terms
          </a>
        </div>
      </div>
    </aside>
  )
}
